import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Head from '@/components/feature/Head';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);
  const category = useMemo(
    () => (comic ? categories.find((cat) => cat.slug === comic.category) : undefined),
    [comic]
  );

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.category === comic.category && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500">未找到该漫画</p>
            <Link to="/" className="text-primary-500 text-sm mt-2 inline-block hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: `${category?.name || ''}漫画`, to: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const articleSchema = {
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-15',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫在线',
      logo: { '@type': 'ImageObject', url: 'https://www.koreancomics.cn/logo.png' },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.koreancomics.cn/article/${comic.id}`,
    },
  };

  return (
    <>
      <Head
        title={`${comic.title} - 韩漫在线免费 | ${category?.name || ''}韩国漫画`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}。韩漫在线免费提供下拉式阅读体验，作者${comic.author}，共${comic.chapters}话，评分${comic.rating}。`}
        keywords={`${comic.title}, ${category?.name || ''}漫画, 韩国漫画, 韩漫在线, ${comic.author}`}
        canonical={`https://www.koreancomics.cn/article/${comic.id}`}
      />

      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Article" data={articleSchema} />

      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-16">
          <Breadcrumbs items={breadcrumbItems} />

          <article className="w-full py-10 md:py-16">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left - Cover */}
                <div className="lg:col-span-1">
                  <div className="sticky top-24">
                    <div className="rounded-xl overflow-hidden aspect-[3/4] max-w-[300px] mx-auto lg:max-w-none">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} 封面 - 韩国漫画`}
                        title={`${comic.title} - 韩漫在线免费`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="mt-4 flex items-center justify-center gap-3">
                      <button className="flex-1 py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-play-circle-line mr-1"></i>
                        开始阅读
                      </button>
                      <button className="w-11 h-11 flex items-center justify-center rounded-lg border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                        <i className="ri-bookmark-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right - Info */}
                <div className="lg:col-span-2">
                  <header className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                      {comic.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {comic.author}
                      </span>
                      <span className="text-foreground-300">|</span>
                      <span className={`font-medium ${comic.status === 'ongoing' ? 'text-primary-500' : 'text-foreground-600'}`}>
                        {comic.status === 'ongoing' ? '连载中' : '已完结'}
                      </span>
                      <span className="text-foreground-300">|</span>
                      <span>共 {comic.chapters} 话</span>
                    </div>
                  </header>

                  {/* Rating & Stats */}
                  <div className="flex flex-wrap items-center gap-4 mb-6 p-4 rounded-xl bg-background-50">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400 text-lg"></i>
                      <span className="text-foreground-900 font-bold text-lg">{comic.rating}</span>
                      <span className="text-foreground-400 text-xs">/5.0</span>
                    </div>
                    <div className="text-foreground-300">|</div>
                    <div className="flex items-center gap-1 text-foreground-500 text-sm">
                      <i className="ri-eye-line"></i>
                      <span>{(comic.views / 10000).toFixed(0)}万 阅读</span>
                    </div>
                    <div className="text-foreground-300">|</div>
                    <div className="text-foreground-500 text-sm">
                      更新于 {comic.updatedAt}
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {comic.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                    {category && (
                      <Link
                        to={`/category/${category.slug}`}
                        className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium whitespace-nowrap hover:bg-primary-100 transition-colors"
                      >
                        {category.name}
                      </Link>
                    )}
                  </div>

                  {/* Description */}
                  <section className="mb-8">
                    <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3">
                      作品简介
                    </h2>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {comic.description}
                    </p>
                  </section>

                  {/* Chapters */}
                  <section>
                    <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">
                      章节列表
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-2">
                      {Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => {
                        const chapterNum = comic.chapters - i;
                        return (
                          <button
                            key={chapterNum}
                            className="flex items-center justify-between px-4 py-2.5 rounded-lg border border-background-200 hover:border-primary-200 hover:bg-primary-50 transition-all text-left cursor-pointer group"
                          >
                            <span className="text-foreground-700 text-sm group-hover:text-primary-600 transition-colors">
                              第{chapterNum}话
                            </span>
                            <span className="text-foreground-400 text-xs">
                              {i === 0 ? '最新' : '免费'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {comic.chapters > 20 && (
                      <p className="text-center text-foreground-400 text-xs mt-4">
                        还有 {comic.chapters - 20} 话未显示
                      </p>
                    )}
                  </section>
                </div>
              </div>

              {/* Related Comics */}
              {relatedComics.length > 0 && (
                <section className="mt-16 pt-10 border-t border-background-200">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                      相关推荐
                    </h2>
                    <Link
                      to={`/category/${comic.category}`}
                      className="text-primary-500 hover:text-primary-600 text-sm font-medium whitespace-nowrap flex items-center gap-1"
                    >
                      更多{category?.name}漫画
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop="">
                    {relatedComics.map((rc) => (
                      <Link
                        key={rc.id}
                        to={`/article/${rc.id}`}
                        className="group block"
                      >
                        <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                          <img
                            src={rc.cover}
                            alt={`${rc.title} - 相关推荐韩国漫画`}
                            title={`${rc.title} - 韩漫在线免费`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-full px-1.5 py-0.5">
                            <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                            <span className="text-white text-[10px] font-medium">{rc.rating}</span>
                          </div>
                        </div>
                        <h3 className="text-foreground-900 font-semibold text-sm line-clamp-1 group-hover:text-primary-500 transition-colors">
                          {rc.title}
                        </h3>
                        <p className="text-foreground-400 text-xs mt-1">{rc.author}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}